import { Button, Col, Popover, Row, Select, Skeleton } from "antd";
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	PicCenterOutlined,
	SearchOutlined,
	SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./searchNumber.module.scss";
import { useTenders } from "../../../tools/api";
import { useLayouts } from "../../layout/layouts";
import { _adr } from "../../../address";
import { complexDigitToman, convertPersian, isEmpty } from "../../../tools/tools";
import { successMessage } from "../../../tools/toastifyShowMessage";
import ListView from "../../../templates/ListView/ListView";
import CardView from "../../../templates/CardView/CardView";
import EmptyData from "../../../components/empty/EmptyData";
import Loading from "../../../components/loader/loading";
import InCreaseWallet from "../../../components/increaseWallet/InCreaseWallet";
import PolicyModal, { AlertTenderModal } from "../details/modal/policyModal";
import LoginRequiredModal from "./modal/loginRequiredModal";
import WinnerModal from "../details/winner/winner-modal";

const EMPTY_DIGITS = Array(7).fill("");
const ACTIVE_PAGE_SIZE = 12;
const INCREASE_CONTEXT_KEY = "aptel-auction-increase-context";
const DEFAULT_ACTIVE_FILTERS = [
	{ id: "all", title: "همه" },
	{ id: "min", title: "کمترین قیمت" },
	{ id: "max", title: "بیشترین قیمت" },
];

const normalizeDigit = (value) =>
	String(convertPersian(value) ?? "")
		.replace(/[^0-9*]/g, "")
		.slice(-1);

// useTenders خروجی Result را برمی‌گرداند؛ این fallback شکل پاسخ کامل را هم پوشش می‌دهد.
const extractAuctionResults = (response) => response?.auctionResult || response?.Result?.auctionResult || [];

const getDisplayNumber = (item) => {
	const title = String(item?.auctionTitle || item?.msisdn || "");
	return title.startsWith("999") ? `0${title}` : title;
};

const normalizeSuggestionItem = (item) => ({
	...item,
	auctionTitle: getDisplayNumber(item),
	auctionProductTypeiId: item.auctionProductTypeId,
	bundle: { bundleName: item.bundleName },
});

const ViewToggle = ({ value, onChange }) => (
	<div className={styles.viewToggle} role="group" aria-label="نوع نمایش">
		<button type="button" className={value === "list" ? styles.activeView : ""} onClick={() => onChange("list")} aria-label="نمایش لیستی">
			<PicCenterOutlined />
		</button>
		<button type="button" className={value === "card" ? styles.activeView : ""} onClick={() => onChange("card")} aria-label="نمایش کارتی">
			<PicCenterOutlined rotate={90} />
		</button>
	</div>
);

const switchTagBag = (type = 0) => {
	switch (type) {
		case 0:
			return "gold";
		case 1:
			return "platinum";
		case 2:
			return "silver";
		case 3:
			return "bronze";
		case 4:
			return "aptel";
		default:
			return "";
	}
};

const SearchNumber = () => {
	const nav = useNavigate();
	const location = useLocation();
	const [searchParam] = useSearchParams();
	const { checkLoginUser, refreshUser } = useLayouts();
	const {
		queryTenderNumbers,
		querySuggestionTenderNumbers,
		startAuctionProcessForMSISDN,
		queryCurrentUserBids,
		queryTenderNumberById,
	} = useTenders();
	const { _currentUser } = useSelector((state) => state?.another);

	const [isLogin, setIsLogin] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	// هفت جایگاه مستقل که در UI به گروه‌های ۱، ۲ و ۴ رقمی نمایش داده می‌شوند.
	const [digits, setDigits] = useState(EMPTY_DIGITS);
	const firstDigitRef = useRef(null);
	const digitRefs = useRef([]);

	//search result
	const [hasSearched, setHasSearched] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const [resultsView, setResultsView] = useState("card");
	const [searchError, setSearchError] = useState("");

	//active tenders preview
	const [activeTenders, setActiveTenders] = useState([]);
	const [isActiveLoading, setIsActiveLoading] = useState(true);
	const [activeError, setActiveError] = useState("");
	const [activeView, setActiveView] = useState("card");
	const [activeFilter, setActiveFilter] = useState("all");
	const [activeVisibleCount, setActiveVisibleCount] = useState(ACTIVE_PAGE_SIZE);

	//start auction flow
	const [selectedItem, setSelectedItem] = useState(null);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [openIncrease, setOpenIncrease] = useState(false);
	const [openLoginRequired, setOpenLoginRequired] = useState(false);
	const [openPolicy, setOpenPolicy] = useState(false);
	const [localInvoiceId, setLocalInvoiceId] = useState("");
	const [increaseMode, setIncreaseMode] = useState("deposit");
	const [winnerChargeAmount, setWinnerChargeAmount] = useState(0);
	const [winnerInfo, setWinnerInfo] = useState({});
	const [winnerIsOpen, setWinnerIsOpen] = useState(false);
	const bankRef = useRef("");
	// refreshUser اطلاعات Redux را تغییر می‌دهد و باعث ساخته‌شدن دوباره توابع LayoutsContext می‌شود؛
	// این guard مانع اجرای دوباره‌ی bootstrap ورود و حلقه‌ی QueryCurrentUser است.
	const loginBootstrapStartedRef = useRef(false);
	//جلوگیری از اجرای چندباره‌ی ادامه‌ی فرآیند بعد از برگشت از صفحه ورود
	const resumedRef = useRef(false);
	const winnerCheckedRef = useRef(false);

	const refreshActiveTenders = async () => {
		setIsActiveLoading(true);
		setActiveError("");
		try {
			const response = await queryTenderNumbers("");
			if (!Array.isArray(response)) throw new Error("Invalid active tenders response");
			setActiveTenders(response);
		} catch (error) {
			console.error(error);
			setActiveError("دریافت مزایده‌های فعال انجام نشد. لطفاً دوباره تلاش کنید.");
		} finally {
			setIsActiveLoading(false);
		}
	};

	useEffect(() => {
		if (loginBootstrapStartedRef.current) return;
		loginBootstrapStartedRef.current = true;

		const checkLogin = async () => {
			const isCheck = await checkLoginUser();
			if (isCheck) {
				await refreshUser(true);
				// ادامه‌ی فرآیند انتخاب شماره باید بعد از دریافت موجودی تازه‌ی کیف پول انجام شود.
				setIsLogin(true);
			}
		};
		void checkLogin();
	}, [checkLoginUser, refreshUser]);

	useEffect(() => {
		const loadInitialActiveTenders = async () => {
			setActiveError("");
			try {
				const response = await queryTenderNumbers("");
				if (!Array.isArray(response)) throw new Error("Invalid active tenders response");
				setActiveTenders(response);
			} catch (error) {
				console.error(error);
				setActiveError("دریافت مزایده‌های فعال انجام نشد. لطفاً دوباره تلاش کنید.");
			} finally {
				setIsActiveLoading(false);
			}
		};
		void loadInitialActiveTenders();
	}, []);

	useEffect(() => {
		if (!isEmpty(searchParam.get("localInvoiceId"))) {
			try {
				const savedContext = JSON.parse(sessionStorage.getItem(INCREASE_CONTEXT_KEY) || "null");
				if (savedContext?.mode) setIncreaseMode(savedContext.mode);
				if (savedContext?.selectedItem) setSelectedItem(savedContext.selectedItem);
				if (savedContext?.winnerChargeAmount) setWinnerChargeAmount(savedContext.winnerChargeAmount);
			} catch (error) {
				console.error(error);
			}
			setLocalInvoiceId(searchParam.get("localInvoiceId"));
			setOpenIncrease(true);
		}
	}, []);

	//برگشت از صفحه ورود: همان شماره‌ای که کاربر مهمان قصد شروع مزایده‌اش را داشت، ادامه پیدا می‌کند
	useEffect(() => {
		const resumeMsisdn = searchParam.get("resumeMsisdn");
		if (!resumedRef.current && isLogin && !isEmpty(resumeMsisdn)) {
			resumedRef.current = true;
			handleResumeAfterLogin(resumeMsisdn);
		}
	}, [isLogin]);

	useEffect(() => {
		if (isLogin && location.state?.winner && !winnerCheckedRef.current) {
			winnerCheckedRef.current = true;
			void checkWinner();
		}
	}, [isLogin, location.state]);

	useEffect(() => {
		setActiveVisibleCount(ACTIVE_PAGE_SIZE);
	}, [activeFilter]);

	const checkWinner = async () => {
		try {
			setIsLoading(true);
			const payload = {
				searchconfigmodel: {
					sort: "",
					filter: "",
					entriesperpage: 10,
					page: 0,
					totalrowcount: 0,
				},
				auctionstatus: [3],
			};
			const userBids = await queryCurrentUserBids(payload);
			const winnerBid = userBids && !userBids?.HasError && Array.isArray(userBids?.Result)
				? userBids.Result.find((item) => item?.hasWon)
				: null;
			if (winnerBid) {
				const tender = await queryTenderNumberById(winnerBid.auctionProductId);
				if (tender) {
					const currentUser = await refreshUser(true);
					setWinnerInfo({ ...currentUser, ...winnerBid, ...tender });
					setWinnerIsOpen(true);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
			if (location.state?.winner) {
				nav(`${location.pathname}${location.search}`, { replace: true, state: null });
			}
		}
	};

	const buildFilter = (values = digits) => values.map((digit) => digit || "*").join("");
	const requiredDeposit = Number(selectedItem?.tenderDeposit) || 0;
	const walletBalance = Number(_currentUser?.walletAmount) || 0;
	const walletShortfall = Math.max(requiredDeposit - walletBalance, 0);
	const increasePrice = increaseMode === "winner" ? winnerChargeAmount : walletShortfall;
	const increasePlanId = increaseMode === "winner"
		? process.env.REACT_APP_INCREASE_WINNER || process.env.REACT_APP_INCREASE_LOGIN
		: process.env.REACT_APP_INCREASE_DEPOSIT;
	const activeCategories = useMemo(() => {
		const uniqueCategories = new Map();
		activeTenders.forEach((item) => {
			const id = item?.auctionProductTypeiId ?? item?.auctionProductTypeId;
			if (id !== undefined && id !== null && !uniqueCategories.has(String(id))) {
				uniqueCategories.set(String(id), { id: String(id), title: item?.auctionProductType || "سایر" });
			}
		});
		return [...DEFAULT_ACTIVE_FILTERS, ...uniqueCategories.values()];
	}, [activeTenders]);
	const visibleActiveTenders = useMemo(() => {
		if (activeFilter === "min") {
			return [...activeTenders].sort((firstItem, secondItem) => (Number(firstItem?.startPrice) || 0) - (Number(secondItem?.startPrice) || 0));
		}
		if (activeFilter === "max") {
			return [...activeTenders].sort((firstItem, secondItem) => (Number(secondItem?.startPrice) || 0) - (Number(firstItem?.startPrice) || 0));
		}
		if (activeFilter !== "all") {
			return activeTenders.filter((item) => String(item?.auctionProductTypeiId ?? item?.auctionProductTypeId) === activeFilter);
		}
		return activeTenders;
	}, [activeFilter, activeTenders]);
	const pagedActiveTenders = visibleActiveTenders.slice(0, activeVisibleCount);
	const clearPattern = () => {
		setDigits(Array(7).fill(""));
		setHasSearched(false);
		setSearchResults([]);
	};

	const handleDigitChange = (index) => (e) => {
		const value = normalizeDigit(e.target.value);
		const next = [...digits];
		next[index] = value;
		setDigits(next);
		if (value && index < 6) {
			digitRefs.current[index + 1]?.focus();
		}
	};

	const focusDigit = (index) => {
		if (index === 0) firstDigitRef.current?.focus();
		else digitRefs.current[index]?.focus();
	};

	const handleDigitKeyDown = (index) => (e) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			e.preventDefault();
			const next = [...digits];
			next[index - 1] = "";
			setDigits(next);
			focusDigit(index - 1);
		} else if (e.key === "ArrowLeft" && index > 0) {
			e.preventDefault();
			focusDigit(index - 1);
		} else if (e.key === "ArrowRight" && index < 6) {
			e.preventDefault();
			focusDigit(index + 1);
		}
	};

	const handleSearch = async () => {
		try {
			setIsLoading(true);
			setSearchError("");
			const payload = {
				searchConfigModel: {
					sort: "",
					filter: buildFilter(),
					entriesPerPage: 10,
					page: 0,
					totalRowCount: 1,
				},
			};
			const response = await querySuggestionTenderNumbers(payload);
			if (!response) throw new Error("Invalid search response");
			setSearchResults(extractAuctionResults(response));
			setHasSearched(true);
		} catch (error) {
			console.error(error);
			setSearchError("جستجوی شماره انجام نشد. لطفاً اتصال خود را بررسی و دوباره تلاش کنید.");
			setHasSearched(true);
		} finally {
			setIsLoading(false);
		}
	};

	const handlePatternPaste = (e) => {
		const pastedValue = String(convertPersian(e.clipboardData.getData("text")) ?? "")
			.replace(/^0999/, "")
			.replace(/[^0-9*]/g, "")
			.slice(0, 7);
		if (pastedValue.length !== 7 || !["1", "3", "*"].includes(pastedValue[0])) return;
		e.preventDefault();
		setDigits(pastedValue.split("").map((digit) => (digit === "*" ? "" : digit)));
	};

	const handleStartAuctionClick = (item) => {
		setSelectedItem(item);
		if (!isLogin) {
			//هدایت ناگهانی به صفحه ورود تجربه خوبی نیست؛ اول به کاربر توضیح می‌دهیم چرا لازم است
			setOpenLoginRequired(true);
			return;
		}
		const required = Number(item?.tenderDeposit) || 0;
		const currentWalletBalance = Number(_currentUser?.walletAmount) || 0;
		if (currentWalletBalance - required >= 0) {
			setOpenConfirm(true);
		} else {
			setIncreaseMode("deposit");
			setOpenIncrease(true);
		}
	};

	const handleConfirmLoginRequired = (confirmed) => {
		setOpenLoginRequired(false);
		if (confirmed && selectedItem) {
			//شماره‌ی انتخابی را در آدرس برگشت نگه می‌داریم تا بعد از ورود، دقیقاً همان جستجو ادامه پیدا کند
			const returnTo = `${_adr.Root}${_adr.Landing.Index}?resumeMsisdn=${selectedItem.msisdn}`;
			nav(_adr.Root + _adr.Security.Login, { state: { from: returnTo } });
		}
	};

	const handleResumeAfterLogin = async (resumeMsisdn) => {
		try {
			setIsLoading(true);
			const filter = String(resumeMsisdn).slice(3, 10).padEnd(7, "*");
			setDigits(filter.split("").map((digit) => (digit === "*" ? "" : digit)));
			const payload = {
				searchConfigModel: { sort: "", filter, entriesPerPage: 10, page: 0, totalRowCount: 1 },
			};
			const response = await querySuggestionTenderNumbers(payload);
			const results = extractAuctionResults(response);
			setSearchResults(results);
			setHasSearched(true);
			nav(_adr.Root + _adr.Landing.Index, { replace: true });
			const found = results.find((r) => String(r.msisdn) === String(resumeMsisdn));
			if (found && found.auctionStatus === 8) {
				handleStartAuctionClick(found);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfirmStart = async (confirmed) => {
		setOpenConfirm(false);
		if (confirmed && selectedItem) {
			try {
				setIsLoading(true);
				const payload = {
					msisdn: selectedItem.msisdn,
					planId: process.env.REACT_APP_WITHDRAW_PLANID,
				};
				const response = await startAuctionProcessForMSISDN(payload);
				if (response && !response?.HasError) {
					successMessage(response?.Message || "مزایده این شماره با موفقیت فعال شد");
					setSelectedItem(null);
					// پس از فعال‌شدن شماره، نتیجه جستجو، لیست مزایده‌های فعال و موجودی کیف پول
					// هم‌زمان از سرور تازه می‌شوند تا کاربر نیازی به رفرش صفحه نداشته باشد.
					await Promise.all([handleSearch(), refreshActiveTenders(), refreshUser(true)]);
				}
				setIsLoading(false);
			} catch (error) {
				console.error(error);
				setIsLoading(false);
			}
		}
	};

	const handleWinnerCallback = (value) => {
		setWinnerIsOpen(false);
		if (value?.state === "pending") {
			const payableAmount = Number(value.amount) || 0;
			setWinnerChargeAmount(Math.max(payableAmount - walletBalance, 0));
			setIncreaseMode("winner");
			setOpenIncrease(true);
		}
	};

	const clearPaymentReturn = () => {
		const nextParams = new URLSearchParams(searchParam);
		nextParams.delete("localInvoiceId");
		const query = nextParams.toString();
		nav(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true, state: null });
	};

	const handleCallBackCharge = async (value) => {
		setOpenIncrease(false);
		if (value?.length > 0) {
			const context = increaseMode === "winner"
				? { mode: "winner", winnerChargeAmount }
				: { mode: "deposit", selectedItem };
			sessionStorage.setItem(INCREASE_CONTEXT_KEY, JSON.stringify(context));
			bankRef.current.innerHTML = value;
			document.querySelector('form[action="https://asan.shaparak.ir"]').submit();
		} else if (value === false && !isEmpty(localInvoiceId)) {
			try {
				const currentUser = await refreshUser(true);
				clearPaymentReturn();
				if (increaseMode === "deposit" && selectedItem) {
					const currentBalance = Number(currentUser?.walletAmount) || 0;
					if (currentBalance >= (Number(selectedItem.tenderDeposit) || 0)) {
						setOpenConfirm(true);
					}
				} else if (increaseMode === "winner") {
					await checkWinner();
				}
			} finally {
				sessionStorage.removeItem(INCREASE_CONTEXT_KEY);
				setLocalInvoiceId("");
			}
		} else {
			sessionStorage.removeItem(INCREASE_CONTEXT_KEY);
			setLocalInvoiceId("");
		}
	};

	const renderSearchResult = (item, isCardView = false) => {
		const key = item.auctionProductId || item.msisdn;
		if (item.auctionStatus === 1) {
			const normalizedItem = normalizeSuggestionItem(item);
			return isCardView ? <CardView key={key} item={normalizedItem} /> : <ListView key={key} item={normalizedItem} />;
		}

		if (item.auctionStatus === 8) {
			return (
				<div key={key} className={`${styles.startCard} ${isCardView ? styles.cardMode : ""}`} data-type={switchTagBag(item.auctionProductTypeId)}>
					<div className={styles.cardAccent} />
					<div className={styles.body}>
						<div className={styles.cardTopline}>
							<span className={styles.tag}>{item.auctionProductType || "شماره رُند"}</span>
							<span className={styles.readyBadge}>
								<CheckCircleOutlined /> آماده شروع مزایده
							</span>
						</div>
						<div className={styles.number} dir="ltr">
							{getDisplayNumber(item)}
						</div>
						<div className={styles.priceGrid}>
							<div className={styles.price}>
								<span>قیمت شروع مزایده</span>
								<strong>{complexDigitToman(item.startPrice)} <small>تومان</small></strong>
							</div>
							<div className={styles.price}>
								<span>ودیعه موردنیاز</span>
								<strong>{complexDigitToman(item.tenderDeposit)} <small>تومان</small></strong>
							</div>
						</div>
						<div className={styles.cardFooter}>
							<p className={styles.desc}>
								<SafetyCertificateOutlined /> ودیعه تا پایان مزایده در کیف پول شما محفوظ می‌ماند.
							</p>
							<div className={`holder-button ${styles.startAction}`}>
								<Button className="btn-aptel" onClick={() => handleStartAuctionClick(item)}>
									شروع مزایده
								</Button>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div key={key} className={`${styles.unavailableRow} ${isCardView ? styles.unavailableCard : ""}`}>
				<span className={styles.unavailableIcon}><CloseCircleOutlined /></span>
				<span className={styles.unavailableNumber} dir="ltr">{getDisplayNumber(item)}</span>
				<span className={styles.unavailableText}>این شماره در حال حاضر قابل شرکت در مزایده نیست</span>
			</div>
		);
	};

	return (
		<>
			<section className={styles.searchHero} aria-labelledby="search-title">
				<h1 id="search-title">سیم‌کارت رُند دلخواهت را پیدا کن</h1>
				<p>رقم‌های موردنظرت را وارد کن و بین شماره‌های موجود بگرد.</p>
				<form
					className={styles.searchForm}
					onSubmit={(e) => {
						e.preventDefault();
						handleSearch();
					}}
				>
					<div className={styles.searchControls}>
						<div className={styles.patternField}>
							<label className={styles.patternLabel} htmlFor="search-digit-0">
								الگوی شماره موبایل
							</label>
							<div className={styles.patternRow} dir="ltr" onPaste={handlePatternPaste}>
								<div className={styles.prefixInput} aria-label="پیش‌شماره ثابت">
									0999
								</div>
								<Select
									ref={firstDigitRef}
									id="search-digit-0"
									className={styles.firstDigitSelect}
									value={digits[0] || undefined}
									placeholder="*"
									onChange={(value) => {
										const next = [...digits];
										next[0] = value === "*" ? "" : value;
										setDigits(next);
										digitRefs.current[1]?.focus();
									}}
									options={[
										{ value: "*", label: "*" },
										{ value: "1", label: "1" },
										{ value: "3", label: "3" },
									]}
									aria-label="رقم اول بعد از پیش‌شماره"
								/>
								<div className={styles.digitGroup} aria-label="دو رقم میانی شماره">
									{[1, 2].map((index) => (
										<input
											key={index}
											ref={(element) => (digitRefs.current[index] = element)}
											className={styles.digitSlot}
											value={digits[index]}
											maxLength={1}
											placeholder="*"
											inputMode="numeric"
											autoComplete="off"
											aria-label={`رقم ${index + 1} بعد از پیش‌شماره`}
											onChange={handleDigitChange(index)}
											onKeyDown={handleDigitKeyDown(index)}
											onFocus={(e) => e.currentTarget.select()}
										/>
									))}
								</div>
								<div className={`${styles.digitGroup} ${styles.lastDigitGroup}`} aria-label="چهار رقم پایانی شماره">
									{[3, 4, 5, 6].map((index) => (
										<input
											key={index}
											ref={(element) => (digitRefs.current[index] = element)}
											className={styles.digitSlot}
											value={digits[index]}
											maxLength={1}
											placeholder="*"
											inputMode="numeric"
											autoComplete="off"
											aria-label={`رقم ${index + 1} بعد از پیش‌شماره`}
											onChange={handleDigitChange(index)}
											onKeyDown={handleDigitKeyDown(index)}
											onFocus={(e) => e.currentTarget.select()}
										/>
									))}
								</div>
							</div>
						</div>
						<Button htmlType="submit" loading={isLoading} className={styles.searchButton} icon={<SearchOutlined />}>
							جستجوی شماره‌ها
						</Button>
					</div>
					<div className={styles.utilityRow}>
						<div className={styles.hintRow}>
							<InfoCircleOutlined />
							<span>علامت * یعنی آن رقم برایت فرقی ندارد.</span>
							<Popover
								placement="bottom"
								content={
									<div style={{ maxWidth: "16rem" }}>
										شماره‌های آپتل با پیش‌شماره 0999 آغاز می‌شوند. رقم اول می‌تواند ۱، ۳ یا * باشد. رقم‌های خالی نیز به‌صورت * جستجو می‌شوند.
									</div>
								}
							>
								<button type="button" className={styles.guideLink}>
									راهنمای جستجو
								</button>
							</Popover>
						</div>
						{digits.some(Boolean) && (
							<Button type="text" icon={<DeleteOutlined />} className={styles.clearButton} onClick={clearPattern} disabled={isLoading}>
								پاک کردن الگو
							</Button>
						)}
					</div>
				</form>
			</section>
			<div className={styles.policyAccess}>
				<SafetyCertificateOutlined />
				<button type="button" onClick={() => setOpenPolicy(true)}>
					مشاهده قوانین و مقررات شرکت در مزایده آنلاین آپتل
				</button>
			</div>

			{hasSearched && (
				<section className={styles.resultsSection} aria-live="polite">
					<div className={styles.sectionHeading}>
						<div>
							<span className={styles.sectionEyebrow}>
								نتایج مطابق با الگوی <b dir="ltr">0999 {buildFilter()}</b>
							</span>
							<h2 className={styles.sectionTitle}>شماره‌های پیشنهادی</h2>
						</div>
						<div className={styles.headingActions}>
							<span className={styles.resultCount}>{searchResults.length} شماره</span>
							<ViewToggle value={resultsView} onChange={setResultsView} />
						</div>
					</div>
					{searchError ? (
						<div className={styles.errorState} role="alert">
							<p>{searchError}</p>
							<Button className="btn-normal" onClick={handleSearch}>تلاش دوباره</Button>
						</div>
					) : searchResults.length === 0 ? (
						<div className={styles.emptyState}>
							<EmptyData text="شماره‌ای با این الگو پیدا نشد" />
							<p>با خالی‌کردن چند خانه، دامنه جستجو را گسترده‌تر کن.</p>
						</div>
					) : (
						<>
							{resultsView === "card" ? (
								<Row gutter={[16, 16]} className={styles.cardGrid}>
									{searchResults.map((item) => (
										<Col key={item.auctionProductId || item.msisdn} xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
											{renderSearchResult(item, true)}
										</Col>
									))}
								</Row>
							) : (
								searchResults.map((item) => renderSearchResult(item))
							)}
						</>
					)}
				</section>
			)}

			<section className={styles.activeSection}>
				<div className={styles.sectionHeading}>
					<div>
						<span className={styles.sectionEyebrow}><ClockCircleOutlined /> فرصت‌های در حال برگزاری</span>
						<h2 className={styles.sectionTitle}>مزایده‌های فعال</h2>
					</div>
					<div className={styles.headingActions}>
						{!isActiveLoading && !activeError && <span className={styles.resultCount}>{visibleActiveTenders.length} مزایده</span>}
						<ViewToggle value={activeView} onChange={setActiveView} />
					</div>
				</div>
				<div className={styles.activeFilters} role="group" aria-label="فیلتر مزایده‌های فعال">
					{activeCategories.map((filter) => (
						<button
							type="button"
							key={filter.id}
							className={activeFilter === filter.id ? styles.activeFilter : ""}
							onClick={() => setActiveFilter(filter.id)}
						>
							{filter.title}
						</button>
					))}
				</div>
				{isActiveLoading ? (
					<div className={styles.activeSkeleton}><Skeleton active paragraph={{ rows: 2 }} /></div>
				) : activeError ? (
					<div className={styles.errorState} role="alert">
						<p>{activeError}</p>
						<Button className="btn-normal" onClick={refreshActiveTenders}>تلاش دوباره</Button>
					</div>
				) : visibleActiveTenders.length > 0 ? activeView === "card" ? (
					<Row gutter={[16, 16]} className={styles.cardGrid}>
						{pagedActiveTenders.map((item) => (
							<Col key={item.auctionProductId || item.msisdn} xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
								<CardView item={item} />
							</Col>
						))}
					</Row>
				) : (
					pagedActiveTenders.map((item) => <ListView key={item.auctionProductId || item.msisdn} item={item} />)
				) : (
					<div className={styles.activeEmpty}><EmptyData text={activeTenders.length ? "مزایده‌ای در این دسته پیدا نشد" : "مزایده فعالی پیدا نشد"} /></div>
				)}
				{!isActiveLoading && !activeError && activeVisibleCount < visibleActiveTenders.length && (
					<div className={styles.viewAllRow}>
						<Button className="btn-normal" onClick={() => setActiveVisibleCount((count) => count + ACTIVE_PAGE_SIZE)}>
							نمایش مزایده‌های بیشتر
						</Button>
					</div>
				)}
			</section>

			<LoginRequiredModal open={openLoginRequired} title={selectedItem?.auctionTitle} callback={handleConfirmLoginRequired} />
			<PolicyModal open={openPolicy} callback={() => setOpenPolicy(false)} />
			<AlertTenderModal
				open={openConfirm}
				price={requiredDeposit}
				auctionNumber={selectedItem ? getDisplayNumber(selectedItem) : ""}
				callback={handleConfirmStart}
				showStartAuctionAcceptance
			/>
			<WinnerModal value={winnerInfo} open={winnerIsOpen} callback={handleWinnerCallback} />
			<InCreaseWallet
				open={openIncrease}
				planid={increasePlanId}
				msisdn={_currentUser?.telnoone}
				localInvoiceId={localInvoiceId}
				price={increasePrice}
				callback={handleCallBackCharge}
				activePrice={true}
			/>
			<div ref={bankRef}></div>
			<Loading isOpen={isLoading} />
		</>
	);
};

export default SearchNumber;
